'use strict';

/**
 * «Tilbake på lager» (blocks/product-back-in-stock.liquid): the portal's TPL API with the theme's own form.
 *
 * - The box stays hidden until the portal's status says visible: GET <api>/status?shopify_variant_id=…&shopify_product_id=…
 *   → { visible, reason, … }. The portal decides (its own stock, tag rules, preorder exclusion); only `visible` is used.
 * - The portal is only asked for variants that are sold out and do not keep selling (data-ask, set in Liquid: available is
 *   false and inventory_policy is not «continue»). Answers are cached for 60 s per variant.
 * - It follows the variant picker through `pippei:variant-change` (assets/product-form.js).
 * - While the box is active the buy control is replaced by the e-mail notification (Knut 2026-10-05):
 *     mobile  – the sticky bar at the bottom shows a compact form instead of the buy control (#sticky-atc-bar [data-tpl-bar]);
 *     desktop – the box itself sticks under the header (.pp-buy-stick, compact while stuck) and the buy control does not.
 *   The page gets data-tpl-active on <html>, the bar gets data-tpl.
 * - Submit: POST <api>/subscribe { shopify_product_id, shopify_variant_id, email, honeypot, page_url }. On success both forms
 *   are replaced by the confirmation (role=status).
 * - The API's debug fields (SKU, stock count) are ignored.
 */
(function () {
  var CACHE_MS = 60000;
  var cache = {};

  function init(root) {
    if (root.__tplReady) return;
    root.__tplReady = true;
    var api = (root.getAttribute('data-api') || '').replace(/\/$/, '');
    if (!api) return;
    var productId = root.getAttribute('data-product-id') || '';
    var ask = (root.getAttribute('data-ask') || '').split(',').filter(Boolean);
    var current = root.getAttribute('data-variant-id') || '';
    var token = 0;
    var done = false;
    var html = document.documentElement;
    var desktop = window.matchMedia('(min-width: 768px)');

    // Two forms share the logic: the box in the page and the compact one in the mobile sticky bar.
    var bar = document.getElementById('sticky-atc-bar');
    var barBox = bar && bar.querySelector('[data-tpl-bar]');
    var uis = [];
    function addUi(form, message) {
      if (!form) return;
      uis.push({
        form: form,
        message: message,
        input: form.querySelector('input[name="email"]'),
        button: form.querySelector('button[type="submit"]')
      });
    }
    addUi(root.querySelector('[data-tpl-form]'), root.querySelector('[data-tpl-message]'));
    if (barBox) addUi(barBox.querySelector('[data-tpl-form-bar]'), barBox.querySelector('[data-tpl-bar-message]'));

    function say(ui, text, tone) {
      if (!ui.message) return;
      ui.message.textContent = text || '';
      ui.message.setAttribute('data-tone', tone || '');
    }
    function sayAll(text, tone) { uis.forEach(function (ui) { say(ui, text, tone); }); }

    // ---- desktop: the box sticks under the header while it is the buy action
    var stickEl = root.closest('.shopify-block') || root.parentElement;
    function updateStuck() {
      if (!stickEl || !stickEl.classList.contains('pp-tpl-stick')) return;
      var header = document.querySelector('.site-header');
      var headerH = header ? header.getBoundingClientRect().height : 0;
      if (headerH) stickEl.style.setProperty('--pp-head-h', headerH + 'px');
      var on = desktop.matches && window.scrollY > 0 && !root.hidden && stickEl.getBoundingClientRect().top <= headerH + 1;
      stickEl.classList.toggle('is-stuck', on);
    }
    window.addEventListener('scroll', updateStuck, { passive: true });
    window.addEventListener('resize', updateStuck);

    function setActive(on) {
      root.hidden = !on;
      html.toggleAttribute('data-tpl-active', on);
      if (bar) bar.toggleAttribute('data-tpl', on);
      if (barBox) barBox.hidden = !on;
      if (stickEl) {
        stickEl.classList.toggle('pp-buy-stick', on);
        stickEl.classList.toggle('pp-tpl-stick', on);
        if (!on) { stickEl.classList.remove('is-stuck'); stickEl.style.removeProperty('--pp-head-h'); }
      }
      if (window.PippeiBuy && window.PippeiBuy.updateSticky) window.PippeiBuy.updateSticky();
      updateStuck();
    }

    function status(variantId) {
      var hit = cache[variantId];
      if (hit && Date.now() - hit.time < CACHE_MS) return Promise.resolve(hit.visible);
      var q = new URLSearchParams();
      q.set('shopify_variant_id', variantId);
      if (productId) q.set('shopify_product_id', productId);
      return fetch(api + '/status?' + q.toString(), { headers: { Accept: 'application/json' }, credentials: 'omit' })
        .then(function (r) { if (!r.ok) throw new Error('status ' + r.status); return r.json(); })
        .then(function (j) {
          var visible = !!(j && j.visible);
          cache[variantId] = { time: Date.now(), visible: visible };
          return visible;
        });
    }

    function resetForms() {
      done = false;
      uis.forEach(function (ui) { ui.form.hidden = false; });
      sayAll('');
    }

    function update(variantId) {
      current = String(variantId || '');
      var mine = ++token;
      resetForms();
      if (!current || ask.indexOf(current) === -1) { setActive(false); return; }
      status(current).then(function (visible) {
        if (mine === token) setActive(visible);
      }).catch(function () {
        if (mine === token) setActive(false);
      });
    }

    function submit(ui) {
      if (done) return;
      var email = (ui.input.value || '').trim();
      if (!email || !ui.input.checkValidity()) {
        ui.input.setAttribute('aria-invalid', 'true');
        say(ui, root.getAttribute('data-msg-invalid'), 'error');
        ui.input.focus();
        return;
      }
      ui.input.removeAttribute('aria-invalid');
      var label = ui.button.getAttribute('data-label') || ui.button.textContent;
      ui.button.disabled = true;
      ui.button.textContent = root.getAttribute('data-label-sending') || label;
      say(ui, '');
      var hp = ui.form.querySelector('input[name="honeypot"]');
      fetch(api + '/subscribe', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({
          shopify_product_id: productId ? Number(productId) : null,
          shopify_variant_id: Number(current),
          email: email,
          honeypot: hp ? hp.value : '',
          page_url: window.location.href
        })
      }).then(function (r) {
        if (r.ok) return r.json();
        var err = new Error('subscribe ' + r.status);
        err.status = r.status;
        throw err;
      }).then(function () {
        done = true;
        uis.forEach(function (u) { u.form.hidden = true; });
        sayAll(root.getAttribute('data-msg-success'), 'success');
        if (ui.message) ui.message.focus();
      }).catch(function (err) {
        var key = 'data-msg-error';
        if (err && err.status === 429) key = 'data-msg-busy';
        else if (err && err.status === 422) key = 'data-msg-unavailable';
        say(ui, root.getAttribute(key), 'error');
      }).then(function () {
        ui.button.disabled = false;
        ui.button.textContent = label;
      });
    }

    uis.forEach(function (ui) {
      ui.form.addEventListener('submit', function (e) { e.preventDefault(); submit(ui); });
      if (ui.input) ui.input.addEventListener('input', function () { ui.input.removeAttribute('aria-invalid'); });
    });

    document.addEventListener('pippei:variant-change', function (e) {
      var v = e.detail && e.detail.variant;
      if (v && String(v.id) !== current) update(v.id);
    });

    update(current);
  }

  function run() { [].slice.call(document.querySelectorAll('[data-tpl]')).forEach(init); }
  run();
  document.addEventListener('shopify:section:load', run);
  document.addEventListener('shopify:block:select', run);
})();
